import { forwardRef } from 'react';
import cx from 'classnames';
import InputGroup from 'react-bootstrap/InputGroup';

import { Button } from '../../buttons';
import SimpleMaskInput from '../SimpleMaskInput';
import type { SimpleMaskInputProps, SimpleMaskInputRef } from '../SimpleMaskInput';

/**
 * Поле с маской даты/времени и кнопками «очистить»/«календарь».
 * Используется как customInput react-datepicker (в DateTimeInput): часть пропсов
 * (value, onChange, name и т.д.) инжектируется DatePicker'ом через cloneElement,
 * остальное приходит через inputProps из DateTimeInput.
 */
export interface DateTimeMaskProps extends Omit<SimpleMaskInputProps, 'value'> {
  /** Инстанс react-datepicker (ref DateTimeInput): управление календарём и очисткой. */
  picker?: {
    isCalendarOpen: () => boolean;
    clear: () => void;
    setOpen: (open?: boolean, skipSetBlur?: boolean) => void;
  };
  /** Дополнительный колбэк после очистки (в дополнение к штатной clear() пикера). */
  onClear?: () => void;
  /** Показывать кнопку-крестик очистки. @default true */
  isClearable?: boolean;
  /** Инжектируется DatePicker'ом (cloneElement customInput). */
  value?: string;
}

const DateTimeMask = forwardRef<SimpleMaskInputRef, DateTimeMaskProps>((props, ref) => {
  const {
    value,
    readOnly,
    disabled,
    picker,
    onClear,
    isClearable = true,
    onChange,
    onManualChange,
    ...rest
  } = props;

  const { isCalendarOpen, clear, setOpen } = picker || {};

  const handleInputClear = () => {
    clear();

    if (typeof onClear === 'function') {
      onClear();
    }
  };

  return (
    <SimpleMaskInput
      ref={ ref }
      { ...rest }
      value={ value }
      disabled={ disabled }
      readOnly={ readOnly }
      className={ cx({ 'has-controls': !readOnly }) }
      onChange={ onChange }
      onManualChange={ onManualChange } // передаем обработчик ручного ввода
    >
      { !readOnly && (
        <InputGroup.Append>
          { value && !disabled && isClearable && (
            <Button
              icon='times'
              className='btn-clear'
              onClick={ handleInputClear }
            />
          ) }

          <Button
            icon='calendar-day'
            className='btn-calendar'
            disabled={ disabled }
            onClick={ () => setOpen(!isCalendarOpen()) }
            variant='outline-primary'
          />
        </InputGroup.Append>
      ) }
    </SimpleMaskInput>
  );
});

DateTimeMask.displayName = 'DateTimeMask';

export default DateTimeMask;
